import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import Table from '~/shared/components/Table';
import { useXFetch } from '~/shared/utils/useXFetch';
import { OptLogVolumeActionsCountResponse, OptLogVolumeActionsResponse } from '~/shared/types/api';
import { TABLE_LIVE_REFRESH_MS } from '~/shared/constants/polling';

import { getOperationColumnDefs } from '../../ReportTable/columnDefs';

import OptimizationActions from './OptimizationActions';

interface OptimizationTableProps {
  tableQueryUrl: string;
  countQueryUrl: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: { field: string; dir: 'desc' | 'asc' }) => void;
  dataLimit: number;
  currentSortField: { field: string; dir: 'desc' | 'asc' };
  currentOffset: string;
  timeSignature: string;
  filtersSignature: string;
}

export default function OptimizationTable({
  tableQueryUrl,
  countQueryUrl,
  onPageChange,
  onSortChange,
  dataLimit,
  currentSortField,
  currentOffset,
  timeSignature,
  filtersSignature,
}: OptimizationTableProps) {
  const api = useXFetch();

  const { data, isFetching, isPlaceholderData, isLoading } = useQuery({
    queryKey: [
      'getOptimizationActions',
      tableQueryUrl,
      countQueryUrl,
      currentOffset,
      timeSignature,
      filtersSignature,
    ],
    refetchOnWindowFocus: false,
    enabled: !!tableQueryUrl && !!countQueryUrl,
    refetchInterval: TABLE_LIVE_REFRESH_MS,
    placeholderData: (old, prev) => {
      if (!old || !prev) {
        return undefined;
      }
      const prevTimeSignature = prev.queryKey[4] as string | undefined;
      const prevFiltersSignature = prev.queryKey[5] as string | undefined;
      return prevTimeSignature === timeSignature && prevFiltersSignature === filtersSignature
        ? old
        : undefined;
    },
    queryFn: async () => {
      const actionsResponse = await api<OptLogVolumeActionsResponse>(tableQueryUrl);
      const countResponse = await api<OptLogVolumeActionsCountResponse>(countQueryUrl);
      const actions = actionsResponse.actions ?? [];
      const count = countResponse.count ?? 0;
      return { actions, count };
    },
  });

  const operationColumnDefs = useMemo(
    () => getOperationColumnDefs({ sortField: currentSortField }),
    [currentSortField]
  );

  const rowData = data?.actions;

  const lastKnownCountRef = useRef<number | undefined>(undefined);
  if (data?.count !== undefined) {
    lastKnownCountRef.current = data.count;
  }
  const stableCount = lastKnownCountRef.current ?? 0;

  const currentPage = useMemo(
    () => Math.floor((parseInt(currentOffset, 10) || 0) / dataLimit),
    [currentOffset, dataLimit]
  );

  const showLoader = (isLoading && !data) || (isFetching && !data && !isPlaceholderData);

  return (
    <div className="flex flex-col gap-[14px] mt-4">
      <div>
        <OptimizationActions
          totalActionsCount={stableCount}
          totalActionsCountIsLoading={isLoading}
          growActions={0}
          shrinkActions={0}
        />
      </div>

      <div className="flex flex-col pb-[14px] h-[520px] 2xl:h-[702px] ">
        <Table
          rowData={rowData}
          columnDefs={operationColumnDefs}
          defaultColDef={{ filter: false }}
          headerHeight={48}
          onSortChange={onSortChange}
          itemsName={'actions'}
          noResultsText="No Results"
          pagination={{
            dataCount: stableCount,
            perPage: dataLimit,
            onChange: onPageChange,
            totalPages: Math.ceil(stableCount / dataLimit) || 1,
            currentPage,
          }}
          loading={showLoader}
          suppressNoRowsWhileLoading
        />
      </div>
    </div>
  );
}
